/**
 * Detalhe do lead (`/leads/:leadId`): quem e, o que o motor fez, o que a
 * pessoa pode fazer agora.
 *
 * Estrutura: HeroBanner com identidade (nome completo -- unica tela que pode;
 * telefone mascarado com "Revelar"), a FRASE EXECUTIVA logo abaixo (o lead
 * inteiro em uma linha), depois duas colunas em `lg`: a linha do tempo a
 * esquerda (2/3), com marco zero, agrupamento por dia e filtro por tipo; a
 * direita, "o que fazer agora", dados, regua (plano completo a partir do
 * fato) e acoes.
 *
 * Decisoes nao obvias:
 *  - A janela do cabecalho NAO recorta a linha do tempo: um lead e uma
 *    entidade, e esconder o pagamento porque a janela e "hoje" contaria uma
 *    historia falsa. O periodo (e os filtros da lista) continuam na URL para
 *    o botao "voltar" cair na mesma lista, no mesmo recorte.
 *  - `company.id` entra nas dependencias do fetcher: a administracao troca a
 *    empresa e a tela recarrega, em vez de misturar comissao de uma com
 *    timeline de outra.
 *  - Interacoes da previa (pausar, remover) sao estado local chaveado pelo
 *    id do lead; trocar de lead zera tudo. Nada finge rede.
 *  - "Marcar venda pela vendedora" e a venda assistida de verdade: o mesmo
 *    dialogo e a mesma `registrarVendaAssistida` da tela Comercial. O evento
 *    local na linha do tempo so nasce com desfecho de sucesso (creditado,
 *    revertido, sem contato) e so na previa; em producao a pagina recarrega
 *    e a linha do tempo vem do servidor.
 *  - "Agora" e `AGORA_SIMULADO` quando o dado e simulado e o relogio real
 *    quando vem do servidor: e o que faz "há 12 min" bater com as datas.
 *  - Situacao, frase, plano e sugestoes saem de `resumo.ts`, as mesmas
 *    funcoes que a lista usa: a tabela e o detalhe nunca discordam.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StatusPill } from '../../../components/LeadsTable';
import { EmptyState, ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, HeroBanner, LinkButton, SectionCard } from '../../../components/ui';
import { AGORA_SIMULADO, useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatBRLFromCents, formatDateTime, PROVIDER_LABEL, VALOR_AUSENTE } from '../../../lib/format';
import { equipeDaEmpresa, ofertasDaEmpresa } from '../../../lib/mock/catalogo';
import type { ProductOffer } from '../../../types';
import { fetchLeadDetail } from '../api';
import { CartaoAcoes } from '../components/CartaoAcoes';
import { CartaoDadosDoLead } from '../components/CartaoDadosDoLead';
import { CartaoRegua, situacaoDaRegua } from '../components/CartaoRegua';
import { LinhaDoTempo } from '../components/LinhaDoTempo';
import { ModalConfirmar } from '../components/ModalConfirmar';
import { ModalMarcarVenda, type VendaMarcada } from '../components/ModalMarcarVenda';
import { ResumoExecutivo } from '../components/ResumoExecutivo';
import { SugestoesDoLead } from '../components/SugestoesDoLead';
import { TelefoneRevelavel } from '../components/TelefoneRevelavel';
import type { ToqueDoMotor } from '../../comercial/types';
import { aoContato } from '../../comercial/vendaAssistida';
import { contatosQueContam, resumoExecutivo, situacaoDoLead, sugestoesDoLead, type EstadoLocalDoLead } from '../resumo';
import type { LeadDetail, TimelineEvent } from '../types';
import { CANAL, FATO, FUNIL_LABEL, SITUACAO } from '../vocabulario';

interface EstadoLocal {
  readonly leadId: string;
  readonly pausada: boolean;
  readonly removida: boolean;
  readonly venda: VendaMarcada | null;
  readonly eventos: readonly TimelineEvent[];
}

function estadoInicial(leadId: string): EstadoLocal {
  return { leadId, pausada: false, removida: false, venda: null, eventos: [] };
}

export function LeadDetalhe(): JSX.Element {
  const { leadId } = useParams<{ leadId: string }>();
  const { company, isPlatformAdmin } = useCompany();
  const idAtual = leadId ?? '';

  const buscar = useCallback((signal: AbortSignal) => fetchLeadDetail(company.id, idAtual, signal), [company.id, idAtual]);
  const { state, reload } = useApiResource(buscar);

  const lead = state.status === 'success' ? state.data : null;

  useEffect(() => {
    const nome = lead?.name ?? 'Lead';
    document.title = `${nome} — Leads · ${company.name} · Converte Fácil`;
  }, [lead?.name, company.name]);

  // Estado local da previa, zerado quando o id muda (ver cabecalho).
  const [local, setLocal] = useState<EstadoLocal>(() => estadoInicial(idAtual));
  const estadoDoLead = local.leadId === idAtual ? local : estadoInicial(idAtual);
  useEffect(() => {
    setLocal((anterior) => (anterior.leadId === idAtual ? anterior : estadoInicial(idAtual)));
  }, [idAtual]);

  const [modalVenda, setModalVenda] = useState(false);
  const [modalRemover, setModalRemover] = useState(false);
  const fecharModalVenda = useCallback((): void => setModalVenda(false), []);

  const agora = useMemo(() => (state.status === 'success' && state.simulated ? AGORA_SIMULADO : new Date()), [state]);
  const equipe = useMemo(() => equipeDaEmpresa(company.id), [company.id]);

  // Ultimo toque do MOTOR na linha do tempo: e a quem a venda assistida credita (ADR-001 E5).
  const ultimoToque = useMemo((): ToqueDoMotor | null => {
    if (lead === null) {
      return null;
    }
    const contatos = contatosQueContam(lead.timeline);
    const ultimo = contatos[contatos.length - 1];
    return ultimo === undefined ? null : { at: ultimo.at, rotulo: `${CANAL[ultimo.channel].label} ${ultimo.touchLabel}`.trim() };
  }, [lead]);

  const oferta: ProductOffer | null = useMemo(() => {
    if (lead === null || lead.offerId === null) {
      return null;
    }
    return ofertasDaEmpresa(company.id).find((o) => o.id === lead.offerId) ?? null;
  }, [lead, company.id]);

  const pausar = (pausada: boolean): void => {
    setLocal((anterior) => ({ ...(anterior.leadId === idAtual ? anterior : estadoInicial(idAtual)), pausada }));
  };

  const simulado = state.status === 'success' && state.simulated;

  // So chega aqui com desfecho de sucesso (o dialogo nao repassa erro nem `duplicado`).
  // O dialogo continua aberto mostrando o desfecho; quem fecha e a pessoa.
  const registrarVenda = (venda: VendaMarcada): void => {
    const evento: TimelineEvent | null = simulado
      ? {
          id: `${idAtual}-previa-venda-${venda.orderRef}`,
          at: venda.paidAt,
          kind: 'seller_sale',
          sellerName: venda.sellerName,
          amountCents: venda.amountCents,
          orderRef: venda.orderRef,
          outcome: venda.desfecho,
          creditedTouch: venda.creditadoA,
          commissionCents: venda.commissionCents,
          feeBpsApplied: venda.feeBpsApplied,
        }
      : null;
    setLocal((anterior) => {
      const base = anterior.leadId === idAtual ? anterior : estadoInicial(idAtual);
      return { ...base, venda, eventos: evento === null ? base.eventos : [...base.eventos, evento] };
    });
    if (!simulado) {
      reload();
    }
  };

  const remover = (): void => {
    const evento: TimelineEvent = {
      id: `${idAtual}-previa-remocao-${Date.now()}`,
      at: new Date().toISOString(),
      kind: 'rule_removed',
      reason: 'Removido manualmente pelo painel. Nenhum toque será enviado; o lead continua no histórico.',
    };
    setLocal((anterior) => {
      const base = anterior.leadId === idAtual ? anterior : estadoInicial(idAtual);
      return { ...base, removida: true, pausada: false, eventos: [...base.eventos, evento] };
    });
    setModalRemover(false);
  };

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      {state.status === 'loading' ? (
        <>
          {/* O h1 existe desde o primeiro quadro: o `aria-labelledby` da secao nunca aponta para um id ausente. */}
          <HeroBanner icon="leads" title="Lead" description="Carregando a linha do tempo deste lead…" backTo={<BotaoVoltar />} aside={<RefreshingDot active />} />
          <Esqueleto />
        </>
      ) : null}

      {state.status === 'error' ? (
        <>
          <HeroBanner icon="leads" title="Lead" description="Detalhe e linha do tempo de um carrinho abandonado ou plano vencido." backTo={<BotaoVoltar />} />
          <ErrorPanel title="Não foi possível carregar o lead" error={state.error} onRetry={reload} />
        </>
      ) : null}

      {state.status === 'success' && lead === null ? (
        <>
          <HeroBanner icon="leads" title="Lead não encontrado" description="Nenhum lead com este identificador na empresa em foco." backTo={<BotaoVoltar />} />
          {state.simulated ? <SimulatedNotice>Na prévia existem só os leads fictícios da lista desta empresa.</SimulatedNotice> : null}
          <EmptyState
            title="Lead não encontrado"
            description={
              <>
                O identificador <code className="rounded bg-page px-1 font-mono text-xs">{idAtual === '' ? VALOR_AUSENTE : idAtual}</code> não corresponde a nenhum lead de{' '}
                {company.name}. Ele pode ter sido apagado ou pertencer a outra empresa.
              </>
            }
            action={
              <LinkButton to="/leads" variant="primary" icon="seta-esq">
                Voltar para a lista de leads
              </LinkButton>
            }
          />
        </>
      ) : null}

      {state.status === 'success' && lead !== null ? (
        <Conteudo
          lead={lead}
          oferta={oferta}
          simulated={state.simulated}
          refreshing={state.refreshing}
          agora={agora}
          isPlatformAdmin={isPlatformAdmin}
          estadoLocal={estadoDoLead}
          onPausar={pausar}
          onAbrirVenda={() => setModalVenda(true)}
          onAbrirRemover={() => setModalRemover(true)}
        />
      ) : null}

      {lead === null ? null : (
        <>
          <ModalMarcarVenda
            open={modalVenda}
            onClose={fecharModalVenda}
            onConfirm={registrarVenda}
            equipe={equipe}
            valorSugeridoCents={lead.amountCents}
            feeBps={company.feeBps}
            leadId={lead.id}
            companyId={company.id}
            toque={ultimoToque}
            agora={agora}
            politicaDeEstorno={company.refundPolicy}
          />
          <ModalConfirmar
            open={modalRemover}
            onClose={() => setModalRemover(false)}
            onConfirm={remover}
            title="Remover este lead da régua?"
            description="Nenhum toque automático será enviado para ele. O histórico continua visível e a remoção fica registrada na linha do tempo."
            confirmLabel="Remover da régua"
          >
            <p className="text-sm text-ink-muted">Nesta prévia a remoção fica só nesta tela. Em produção ela cancela o job de recuperação (RF05) sem apagar o lead.</p>
          </ModalConfirmar>
        </>
      )}
    </section>
  );
}

function Conteudo({
  lead,
  oferta,
  simulated,
  refreshing,
  agora,
  isPlatformAdmin,
  estadoLocal,
  onPausar,
  onAbrirVenda,
  onAbrirRemover,
}: {
  readonly lead: LeadDetail;
  readonly oferta: ProductOffer | null;
  readonly simulated: boolean;
  readonly refreshing: boolean;
  readonly agora: Date;
  readonly isPlatformAdmin: boolean;
  readonly estadoLocal: EstadoLocal;
  readonly onPausar: (pausada: boolean) => void;
  readonly onAbrirVenda: () => void;
  readonly onAbrirRemover: () => void;
}): JSX.Element {
  // A politica de estorno da empresa em foco decide o texto da comissao (ADR-001 E2).
  const { company } = useCompany();
  // Em producao a venda enviada vem do servidor ao recarregar; o estado local so sobrepoe na previa.
  const localDaRegua: EstadoLocalDoLead = {
    pausada: estadoLocal.pausada,
    removida: estadoLocal.removida,
    vendaMarcada: estadoLocal.venda !== null && simulated,
    vendaDesfecho: simulated ? estadoLocal.venda?.desfecho : undefined,
  };
  const situacaoRegua = situacaoDaRegua(lead, localDaRegua);
  const situacao = situacaoDoLead(lead, localDaRegua);
  const info = SITUACAO[situacao];
  const fato = FATO[lead.funnel];
  const conversaoAtiva = lead.conversion !== null && !lead.conversion.reversed;
  const houveEstorno = lead.timeline.some((e) => e.kind === 'reversal');
  const titulo = lead.name ?? 'Lead sem nome informado';
  const resumo = useMemo(
    () => resumoExecutivo(lead, agora, localDaRegua),
    [lead, agora, localDaRegua.pausada, localDaRegua.removida, localDaRegua.vendaMarcada, localDaRegua.vendaDesfecho],
  );
  const sugestoes = useMemo(
    () => sugestoesDoLead(lead, agora, localDaRegua, company.refundPolicy ?? 'reverte_chargeback'),
    [lead, agora, localDaRegua.pausada, localDaRegua.removida, localDaRegua.vendaMarcada, localDaRegua.vendaDesfecho, company.refundPolicy],
  );
  const venda = estadoLocal.venda;
  // O selo do topo diz o desfecho da venda assistida sem esperar o servidor: e a resposta dele.
  const pill =
    venda !== null && venda.desfecho !== 'creditado'
      ? { tone: 'neutral' as const, label: venda.desfecho === 'sem_contato' ? 'Venda sem comissão' : 'Comissão revertida' }
      : { tone: info.pill, label: info.label };

  return (
    <>
      <HeroBanner
        icon="leads"
        tone={lead.funnel === 'expirados' ? 'brand' : 'accent'}
        title={titulo}
        backTo={<BotaoVoltar />}
        badges={
          <>
            <StatusPill tone={pill.tone} label={pill.label} />
            <Badge tone="neutral">{PROVIDER_LABEL[lead.provider]}</Badge>
            {lead.highValue ? (
              <Badge tone="accent" icon="estrela">
                Alto valor
              </Badge>
            ) : null}
          </>
        }
        description={
          <span className="flex flex-col gap-1">
            <TelefoneRevelavel phoneE164={lead.phoneE164} />
            <span>
              {lead.productName ?? 'Produto não informado pelo provedor'} · <span className="tabular-nums font-medium text-ink">{formatBRLFromCents(lead.amountCents)}</span> · {fato.verbo.toLowerCase()} em{' '}
              <time dateTime={lead.abandonedAt} className="tabular-nums">
                {formatDateTime(lead.abandonedAt)}
              </time>
            </span>
          </span>
        }
        aside={
          <>
            <RefreshingDot active={refreshing} />
            <Badge tone={lead.funnel === 'expirados' ? 'brand' : 'accent'} icon={fato.icone}>
              {FUNIL_LABEL[lead.funnel]}
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do {fato.substantivo}
            </Badge>
            {venda !== null ? (
              <Badge tone={venda.desfecho === 'creditado' ? 'good' : venda.desfecho === 'creditado_revertido' ? 'warning' : 'neutral'} dot>
                Venda assistida · {formatBRLFromCents(venda.amountCents)} · {venda.selo}
                {venda.creditadoA === null || venda.desfecho !== 'creditado' ? '' : ` ${aoContato(venda.creditadoA)}`}
              </Badge>
            ) : conversaoAtiva && lead.conversion !== null ? (
              <Badge tone="good" dot>
                Recuperado em {formatDateTime(lead.conversion.paidAt)} · {CANAL[lead.conversion.creditedTo.channel].label}
              </Badge>
            ) : lead.conversion !== null ? (
              <Badge tone="warning" dot>
                Conversão revertida por estorno
              </Badge>
            ) : lead.recoveredAt !== null ? (
              <Badge tone="info" dot>
                Pagou sozinho em {formatDateTime(lead.recoveredAt)}
              </Badge>
            ) : null}
            {houveEstorno && lead.conversion === null ? <Badge tone="warning">Reembolsado</Badge> : null}
          </>
        }
      />

      {simulated ? <SimulatedNotice>Este lead é fictício. A linha do tempo mostra as situações que o motor produz de verdade, com nomes e valores inventados.</SimulatedNotice> : null}

      <ResumoExecutivo resumo={resumo} agora={agora} />

      <div className="grid gap-6 lg:grid-cols-3">
        <SectionCard
          id="linha-do-tempo"
          title="Linha do tempo"
          icon="relogio"
          description={`Do ${fato.substantivo} ao desfecho, em ordem, agrupado por dia. Cada evento mostra a distância até o marco zero.`}
          className="lg:col-span-2"
          aside={
            <Badge tone={situacaoRegua.tone} dot>
              {situacaoRegua.label}
            </Badge>
          }
        >
          <LinhaDoTempo lead={lead} locais={estadoLocal.eventos} agora={agora} />
        </SectionCard>

        <div className="flex flex-col gap-6">
          <SugestoesDoLead sugestoes={sugestoes} isPlatformAdmin={isPlatformAdmin} onMarcarVenda={onAbrirVenda} />
          <CartaoDadosDoLead lead={lead} oferta={oferta} />
          <CartaoRegua lead={lead} local={localDaRegua} agora={agora} onPausar={onPausar} />
          <CartaoAcoes
            leadId={lead.id}
            isPlatformAdmin={isPlatformAdmin}
            podeMarcarVenda={!conversaoAtiva && venda === null}
            vendaMarcada={venda !== null}
            rotuloVenda={venda === null ? undefined : `${simulated ? 'Registrado na prévia' : 'Enviada ao motor'} · ${venda.selo}`}
            podeRemover={situacaoRegua.viva}
            removida={estadoLocal.removida}
            onMarcarVenda={onAbrirVenda}
            onRemover={onAbrirRemover}
          />
        </div>
      </div>
    </>
  );
}

function BotaoVoltar(): JSX.Element {
  return (
    <LinkButton to="/leads" variant="ghost" size="sm" icon="seta-esq" className="shrink-0">
      Leads
    </LinkButton>
  );
}

function Esqueleto(): JSX.Element {
  return (
    <>
      <p role="status" className="sr-only">
        Carregando o lead
      </p>
      <div aria-hidden="true" className="flex flex-col gap-6">
        <div className="h-20 animate-pulse rounded-2xl border border-line bg-elevated" />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="h-96 animate-pulse rounded-2xl border border-line bg-surface lg:col-span-2" />
          <div className="flex flex-col gap-6">
            <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
            <div className="h-44 animate-pulse rounded-2xl border border-line bg-surface" />
            <div className="h-72 animate-pulse rounded-2xl border border-line bg-surface" />
          </div>
        </div>
      </div>
    </>
  );
}
